/**
 * 前端 API 請求客戶端
 * 封裝 fetch，自動處理認證 Token 和錯誤
 */

/** API 回應基礎結構 */
interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
}

/**
 * API 錯誤回應結構（與後端 lib/response.ts errorResponse 對應）：
 * `error` 是物件，包含 code/message/details，details 也可能放在物件內。
 */
interface ApiErrorResponse {
  success: false;
  error:
    | string
    | {
        code: string;
        message: string;
        details?: unknown;
      };
  details?: unknown;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

/** API 錯誤類 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * 前端與 API 永遠同源：dev 透過 Vite proxy 把 `/api/*` 轉發到 Hono（localhost:3334）；
 * production 透過 Nginx 反向代理。所以這裡只發相對路徑，credentials 用 same-origin。
 */

/* ==================== Dev 環境請求日誌 ==================== */

/**
 * 是否啟用 API 請求日誌：
 *   - 預設只在 Vite DEV 模式啟用，production build 完全 tree-shake 掉
 *   - 想在 dev 暫時關閉：DevTools 跑 `localStorage.setItem('api-log', 'off')`
 *   - 想在非 dev 環境臨時開啟：`localStorage.setItem('api-log', 'on')`
 */
function isApiLogEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const flag = window.localStorage?.getItem('api-log');
    if (flag === 'off') return false;
    if (flag === 'on') return true;
  } catch {
    /* localStorage 被禁用時忽略 */
  }
  return import.meta.env.DEV;
}

/** 從 fetch body 反推 payload，方便日誌展示 */
function extractPayload(body: BodyInit | null | undefined): unknown {
  if (body == null) return undefined;
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }
  /* FormData / Blob / ArrayBuffer 等只標註類型，不嘗試序列化 */
  return `(non-JSON body: ${(body as object).constructor?.name ?? typeof body})`;
}

/** 安全 JSON 序列化（過濾循環引用） */
function safeStringify(value: unknown): string {
  if (value === undefined) return '(none)';
  try {
    const seen = new WeakSet<object>();
    return JSON.stringify(
      value,
      (_key, val: unknown) => {
        if (typeof val === 'object' && val !== null) {
          if (seen.has(val)) return '[Circular]';
          seen.add(val);
        }
        return val;
      },
      2,
    );
  } catch {
    return '[Unserializable]';
  }
}

/**
 * 在 console 打印 URL / Payload / Response，
 * 整理成一段帶分隔線的文字塊，方便右鍵 Copy message 整體貼給 AI 分析。
 */
function logApiCall(params: {
  method: string;
  url: string;
  payload: unknown;
  response: unknown;
  status: number;
  durationMs: number;
  ok: boolean;
  networkError?: unknown;
}): void {
  if (!isApiLogEnabled()) return;
  const { method, url, payload, response, status, durationMs, ok, networkError } = params;

  const tag = networkError ? '[API NET-ERR]' : ok ? '[API OK]' : '[API ERR]';
  const titleColor = networkError || !ok ? '#ef4444' : '#10b981';
  const title = `${tag} ${method} ${url} → ${networkError ? 'network error' : status} (${durationMs}ms)`;

  const block = [
    '═══════════ API CALL (copy below for AI) ═══════════',
    `URL     : ${method} ${url}`,
    `Status  : ${networkError ? 'network error' : status}`,
    `Time    : ${durationMs}ms`,
    '',
    'Payload :',
    safeStringify(payload),
    '',
    networkError ? 'NetworkError:' : 'Response:',
    networkError
      ? networkError instanceof Error
        ? networkError.message
        : safeStringify(networkError)
      : safeStringify(response),
    '════════════════════════════════════════════════════',
  ].join('\n');

  console.groupCollapsed(`%c${title}`, `color:${titleColor};font-weight:bold;`);
  console.log(block);
  console.groupEnd();
}

/* ========================================================== */

/** 是否正在刷新 Token */
let isRefreshing = false;
/** 等待 Token 刷新的請求佇列 */
let refreshSubscribers: ((success: boolean) => void)[] = [];

/**
 * 將請求加入等待佇列
 */
function subscribeTokenRefresh(): Promise<boolean> {
  return new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });
}

/**
 * 通知所有等待中的請求
 */
function onTokenRefreshed(success: boolean) {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
}

/**
 * 嘗試刷新 Token
 */
async function tryRefreshToken(): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
    });
    return response.ok;
  } catch (err) {
    console.error('[api-client] refresh token request failed', err);
    return false;
  }
}

/**
 * 核心請求函數
 * @param url - API 路徑
 * @param options - fetch 選項
 * @returns 回應數據
 */
async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const method = (options.method ?? 'GET').toUpperCase();
  const payloadForLog = extractPayload(options.body);

  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    /* dev (Vite proxy) 與 production (Nginx) 都同源，same-origin 即可帶 cookie */
    credentials: 'same-origin',
  };

  let response: Response;
  try {
    response = await fetch(url, config);
  } catch (networkErr) {
    /* fetch 自身拋錯（網絡斷開、CORS、被 abort 等），記錄一條 NET-ERR 後再拋 */
    logApiCall({
      method,
      url,
      payload: payloadForLog,
      response: undefined,
      status: 0,
      durationMs: Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
      ),
      ok: false,
      networkError: networkErr,
    });
    throw networkErr;
  }

  /* 401 時嘗試刷新 Token */
  if (response.status === 401 && !url.includes('/api/auth/')) {
    if (!isRefreshing) {
      isRefreshing = true;
      const success = await tryRefreshToken();
      isRefreshing = false;
      onTokenRefreshed(success);

      if (success) {
        /* 使用新 Token 重試原始請求 */
        response = await fetch(url, config);
      } else {
        /* 刷新失敗，跳轉登入頁 */
        window.location.href = '/login';
        throw new ApiError('登入已過期，請重新登入', 401);
      }
    } else {
      /* 等待 Token 刷新完成 */
      const success = await subscribeTokenRefresh();
      if (success) {
        response = await fetch(url, config);
      } else {
        throw new ApiError('登入已過期，請重新登入', 401);
      }
    }
  }

  /* 改用 text → JSON.parse，避免非 JSON 響應在日誌前就拋錯 */
  const rawText = await response.text();
  let raw: unknown;
  try {
    raw = rawText ? JSON.parse(rawText) : undefined;
  } catch {
    raw = rawText;
  }

  const durationMs = Math.round(
    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
  );
  const isStandardEnvelope =
    raw !== null && typeof raw === 'object' && 'success' in (raw as Record<string, unknown>);
  const businessOk = !isStandardEnvelope || (raw as { success: boolean }).success !== false;

  logApiCall({
    method,
    url,
    payload: payloadForLog,
    response: raw,
    status: response.status,
    durationMs,
    ok: response.ok && businessOk,
  });

  /* 非 JSON 或缺少標準 envelope：按 HTTP 狀態判定 */
  if (!isStandardEnvelope) {
    if (!response.ok) {
      throw new ApiError(
        typeof raw === 'string' && raw ? raw : `請求失敗（${response.status}）`,
        response.status,
        raw,
      );
    }
    return raw as T;
  }

  const data = raw as ApiResponse<T>;

  if (!data.success) {
    /* 兼容兩種錯誤格式：
     *   1) 後端標準: { success: false, error: { code, message, details? } }
     *   2) 舊版/平鋪: { success: false, error: 'string', details? }
     * 都統一抽出可讀 message，避免直接傳物件導致 toString 變 "[object Object]"。
     */
    const errField = data.error;
    const message =
      typeof errField === 'string'
        ? errField
        : (errField?.message ?? errField?.code ?? `請求失敗（${response.status}）`);
    const details =
      typeof errField === 'string' ? data.details : (errField?.details ?? data.details);
    throw new ApiError(message, response.status, details);
  }

  return data.data;
}

/** API 客戶端 */
export const apiClient = {
  /** GET 請求 */
  get<T>(url: string): Promise<T> {
    return request<T>(url, { method: 'GET' });
  },

  /** POST 請求 */
  post<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /** PUT 請求 */
  put<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /** PATCH 請求 */
  patch<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /** DELETE 請求（可選帶 body） */
  delete<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  },
};

// ─── 合約評審（ZHSR01-12） ──────────────────────────────────────────────────

import type {
  CorrectContractReviewSignatureInput,
  UpdateContractReviewInput,
  LeadSubmitContractReviewInput,
  LeadSignatureCandidateDto,
  ManagerDecideContractReviewInput,
} from '@zhcb/shared';

/**
 * 合約評審單 DTO，對應後端 GET /api/contract-reviews/:id 的 item 結構。
 * 字段保守列出：未來新增可選欄位時前端無需即時更新此型別。
 */
export interface ContractReviewDTO {
  id: number;
  caseId: number;
  reviewCode: string;
  round: number;
  status: 'draft' | 'lead_submitted' | 'manager_approved' | 'rejected';
  conclusion: 'accept' | 'reject_fixable' | 'reject_terminal' | null;
  scopeSnapshot: Record<string, unknown> | null;
  checklist: Array<{ no: number; label: string; answer: 'yes' | 'no'; note?: string }> | null;
  /** 管理組初審人 user id；null 表示尚未初審 */
  adminReviewedBy: number | null;
  /** 管理組初審人姓名（後端 leftJoin users 附帶，僅讀取端點有） */
  adminReviewedByName?: string | null;
  /** 管理組初審時間；非空表示「申請文件齊全」已確認 */
  adminReviewedAt: string | null;
  /** 管理組初審備註 */
  adminReviewNote: string | null;
  leadOpinion: string | null;
  /** 驗證組長決議（序59.4）：accept_taf=受理發TAF證書 / reject=不受理；存量單據為 null */
  leadDecision: 'accept_taf' | 'reject' | null;
  leadUserId: number | null;
  leadSignatureFileId: number | null;
  /** 簽名檔實際歸屬人 user id（序162：管理組代組長導入時與 leadUserId 不同） */
  leadSignatureOwnerId?: number | null;
  /** 組長簽名日期（序59，YYYY-MM-DD；舊資料為 null） */
  leadSignDate?: string | null;
  /** 總經理簽名日期（序60，YYYY-MM-DD；舊資料為 null） */
  gmSignDate?: string | null;
  leadSubmittedAt: string | null;
  managerOpinion: string | null;
  managerUserId: number | null;
  managerSignatureFileId: number | null;
  managerDecidedAt: string | null;
  rejectionReason: string | null;
  auditLanguage: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  createdBy: number | null;
  version: number;
}

/** 統一封裝合約評審相關 API 調用，命名與後端 service 對齊 */
export const contractReviewApi = {
  /** 列出該 case 全部評審單（按 round 升序） */
  list: async (caseId: number): Promise<{ items: ContractReviewDTO[] }> => {
    const res = await apiClient.get<{ items: ContractReviewDTO[] }>(
      `/api/cases/${caseId}/contract-reviews`,
    );
    return res;
  },

  /** 建立新評審單（round 由後端自增） */
  create: async (caseId: number): Promise<{ item: ContractReviewDTO }> => {
    const res = await apiClient.post<{ item: ContractReviewDTO }>(
      `/api/cases/${caseId}/contract-reviews`,
    );
    return res;
  },

  /** 取得單筆評審單 */
  get: async (id: number): Promise<{ item: ContractReviewDTO }> => {
    const res = await apiClient.get<{ item: ContractReviewDTO }>(`/api/contract-reviews/${id}`);
    return res;
  },

  /** 編輯草稿（須帶 version 樂觀鎖） */
  update: async (
    id: number,
    body: UpdateContractReviewInput,
  ): Promise<{ item: ContractReviewDTO }> => {
    const res = await apiClient.patch<{ item: ContractReviewDTO }>(
      `/api/contract-reviews/${id}`,
      body,
    );
    return res;
  },

  /** 管理組初審（確認申請文件齊全；note 可選；僅草稿階段可調用） */
  adminReview: async (
    caseId: number,
    id: number,
    body?: { note?: string },
  ): Promise<{ item: ContractReviewDTO }> => {
    const res = await apiClient.post<{ item: ContractReviewDTO }>(
      `/api/cases/${caseId}/contract-reviews/${id}/admin-review`,
      body ?? {},
    );
    return res;
  },

  /**
   * 驗證組長簽名人選清單（序162）：供管理組協助組長提交時，挑選要導入哪一位
   * 組長／代理人的員工檔案簽名檔，取代只能導入自己簽名檔的限制。
   */
  leadSignatureOptions: async (): Promise<{ items: LeadSignatureCandidateDto[] }> => {
    const res = await apiClient.get<{ items: LeadSignatureCandidateDto[] }>(
      '/api/contract-reviews/lead-signature-options',
    );
    return res;
  },

  /** 組長提交評審（draft → lead_submitted） */
  leadSubmit: async (
    id: number,
    body: LeadSubmitContractReviewInput,
  ): Promise<{ item: ContractReviewDTO }> => {
    const res = await apiClient.post<{ item: ContractReviewDTO }>(
      `/api/contract-reviews/${id}/lead-submit`,
      body,
    );
    return res;
  },

  /** 總經理批示（lead_submitted → manager_approved / rejected） */
  managerDecide: async (
    id: number,
    body: ManagerDecideContractReviewInput,
  ): Promise<{ item: ContractReviewDTO }> => {
    const res = await apiClient.post<{ item: ContractReviewDTO }>(
      `/api/contract-reviews/${id}/manager-decide`,
      body,
    );
    return res;
  },

  /** 現場稽核開始前補正已通過評審的簽名檔／日期，後端同步留存補正歷史。 */
  correctSignature: async (
    id: number,
    body: CorrectContractReviewSignatureInput,
  ): Promise<{ item: ContractReviewDTO }> => {
    return apiClient.patch<{ item: ContractReviewDTO }>(
      `/api/contract-reviews/${id}/signature-correction`,
      body,
    );
  },

  /** 撤回草稿（軟刪除） */
  cancel: async (id: number): Promise<{ ok: true }> => {
    const res = await apiClient.delete<{ ok: true }>(`/api/contract-reviews/${id}`);
    return res;
  },
};
